import React, { useState, useEffect, useRef } from 'react';
import { useSalon } from '../../contexts/SalonContext';
import Button from '../ui/Button';
import { 
  getProfessionals, 
  addProfessional, 
  updateProfessional, 
  deleteProfessional
} from '../../firebase/professionals';
import { 
  getProfessionalServices,
  batchUpdateProfessionalServiceAssociations 
} from '../../firebase/professionalServices';
import { getServices } from '../../firebase/services';
import { getCategories } from '../../firebase/salon';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
// Importar funções relacionadas aos convites
import { useToast } from '../../components/ui/ToastManager';
import { createUserInvitation, getUserInvitationByEmail } from '../../firebase/invitations';

/**
 * Componente para gerenciamento de profissionais
 * Utilizando estilo e componentes do fluxo de criação de contas
 */
const ProfessionalManagement = () => {
  const { salonId } = useSalon();
  const { showToast } = useToast();
  const [professionals, setProfessionals] = useState([]);
  const [pendingProfessionals, setPendingProfessionals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [currentProfessional, setCurrentProfessional] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [activeTab, setActiveTab] = useState('active'); // 'active' ou 'pending'
  const [professionalForm, setProfessionalForm] = useState({
    name: '',
    email: '',
    phone: '',
    photo: null,
    photoURL: '',
    bio: '',
    serviceIds: []
  });

  // Buscar profissionais, categorias e serviços ao carregar
  useEffect(() => {
    const fetchData = async () => {
      if (!salonId) return;
      
      console.log("[ProfessionalManagement] Buscando dados para salonId:", salonId);
      
      try {
        setLoading(true);
        setError(null);
        
        // Buscar profissionais
        const proData = await getProfessionals(salonId);
        console.log(`[ProfessionalManagement] ${proData.length} profissionais encontrados:`, 
          proData.map(p => ({ id: p.id, nome: p.nome || p.name, serviceCount: p.serviceCount })));
        
        // Para cada profissional, verificar se tem o campo serviceCount
        // Se não tiver, inicializar com 0 ou verificar o número real de serviços
        const enhancedProfessionals = await Promise.all(proData.map(async (prof) => {
          if (prof.serviceCount === undefined) {
            console.log(`[ProfessionalManagement] Profissional ${prof.id} não tem serviceCount, verificando serviços...`);
            
            // Buscar serviços usando a nova estrutura
            try {
              const services = await getProfessionalServices(salonId, prof.id);
              const serviceCount = services.length;
              
              console.log(`[ProfessionalManagement] Encontrados ${serviceCount} serviços para profissional ${prof.id}`);
              
              // Atualizar o profissional com o valor correto
              await updateProfessional(salonId, prof.id, { serviceCount });
              
              return { ...prof, serviceCount };
            } catch (err) {
              console.error(`[ProfessionalManagement] Erro ao verificar serviços para profissional ${prof.id}:`, err);
              // Definir como 0 em caso de erro
              return { ...prof, serviceCount: 0 };
            }
          }
          return prof;
        }));
        
        setProfessionals(enhancedProfessionals);
        
        // Buscar categorias
        const catsData = await getCategories(salonId);
        setCategories(catsData);
        
        // Buscar serviços usando a API de serviços em vez de acessar subcoleções
        const servicesData = await getServices(salonId);
        console.log(`[ProfessionalManagement] ${servicesData.length} serviços encontrados`);
        
        // Formatar serviços para o formato usado no onboarding
        const formattedServices = servicesData.map(service => ({
          id: service.id,
          name: service.nome,
          categoryId: service.categoria,
          categoryName: catsData.find(c => c.id === service.categoria)?.nome || '',
          duration: service.duracao,
          price: service.preco,
          professionalCount: service.professionalCount || 0
        }));
        
        console.log("[ProfessionalManagement] Serviços formatados:", formattedServices.map(s => ({
          id: s.id, 
          nome: s.name, 
          professionalCount: s.professionalCount
        })));
        
        setAllServices(formattedServices);
        
        // Buscar convites pendentes para profissionais
        await fetchPendingProfessionals();
      } catch (err) {
        console.error('[ProfessionalManagement] Erro ao carregar dados:', err);
        setError('Falha ao carregar dados: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [salonId]);
  
  // Função para buscar os convites pendentes de profissionais
  const fetchPendingProfessionals = async () => {
    try {
      if (!salonId) return;
      
      console.log("[ProfessionalManagement] Buscando convites pendentes");
      
      // Buscar convites pendentes para o salão
      const invitationsRef = collection(db, 'invitations');
      const q = query(
        invitationsRef,
        where('salonId', '==', salonId),
        where('status', '==', 'pending'),
        where('role', '==', 'professional')
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        console.log("[ProfessionalManagement] Nenhum convite pendente encontrado");
        setPendingProfessionals([]);
        return;
      }
      
      // Transformar documentos em objetos
      const pendingInvitations = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Converter timestamps para objetos Date
        const createdAt = data.createdAt && data.createdAt.toDate ? 
          data.createdAt.toDate() : 
          data.createdAt ? new Date(data.createdAt.seconds * 1000) : new Date();
          
        const expiresAt = data.expiresAt && data.expiresAt.toDate ? 
          data.expiresAt.toDate() : 
          data.expiresAt ? new Date(data.expiresAt.seconds * 1000) : null;
          
        return {
          ...data,
          createdAt,
          expiresAt,
          type: 'invitation'
        };
      });
      
      console.log(`[ProfessionalManagement] ${pendingInvitations.length} convites pendentes encontrados:`, 
        pendingInvitations.map(p => ({ id: p.id, email: p.email })));
      
      setPendingProfessionals(pendingInvitations);
    } catch (err) {
      console.error('[ProfessionalManagement] Erro ao buscar convites pendentes:', err);
      // Não definir erro global para não afetar o fluxo principal
    }
  };

  const handleAddProfessional = () => {
    setShowForm(true);
    setCurrentProfessional(null);
    setProfessionalForm({
      name: '',
      email: '',
      phone: '',
      photo: null,
      photoURL: '',
      bio: '',
      serviceIds: []
    });
  };
  
  const handleEditProfessional = async (professional) => {
    try {
      setLoading(true);
      console.log(`[ProfessionalManagement] Editando profissional: ${professional.id} - ${professional.nome || professional.name}`);
      
      // Buscar serviços associados a este profissional usando a nova função
      console.log(`[ProfessionalManagement] Buscando serviços do profissional: ${professional.id}`);
      const professionalServices = await getProfessionalServices(salonId, professional.id);
      console.log(`[ProfessionalManagement] Serviços encontrados: ${professionalServices.length}`, 
                  professionalServices.map(s => ({id: s.serviceId, nome: s.serviceName})));
      
      const serviceIds = professionalServices.map(service => service.serviceId);
      
      // Se o serviceCount não corresponder ao número real de serviços, atualizar o profissional
      if (professional.serviceCount !== serviceIds.length) {
        console.log(`[ProfessionalManagement] Corrigindo discrepância no serviceCount: armazenado=${professional.serviceCount}, real=${serviceIds.length}`);
        await updateProfessional(salonId, professional.id, {
          serviceCount: serviceIds.length
        });
        
        // Atualizar o professional no estado local também
        professional.serviceCount = serviceIds.length;
      }
      
      setShowForm(true);
      setCurrentProfessional(professional);
      setProfessionalForm({
        name: professional.nome || professional.name || '',
        email: professional.email || '',
        phone: professional.phone || professional.telefone || '',
        photo: null,
        photoURL: professional.photoURL || '',
        bio: professional.bio || '',
        serviceIds: serviceIds || []
      });
      
      console.log("[ProfessionalManagement] Formulário preparado com serviços:", serviceIds);
    } catch (err) {
      console.error('[ProfessionalManagement] Erro ao editar profissional:', err);
      setError('Falha ao editar profissional: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteProfessional = async (professional) => {
    if (window.confirm(`Tem certeza que deseja excluir ${professional.nome || professional.name}?`)) {
      try {
        setLoading(true);
        await deleteProfessional(salonId, professional.id);
        
        // Atualizar lista de profissionais
        setProfessionals(prev => prev.filter(p => p.id !== professional.id));
      } catch (err) {
        console.error('Erro ao excluir profissional:', err);
        setError('Falha ao excluir profissional: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfessionalForm({
      ...professionalForm,
      [name]: value
    });
  };
  
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Criar uma URL para pré-visualização da imagem
    const photoURL = URL.createObjectURL(file);
    
    setProfessionalForm({
      ...professionalForm,
      photo: file,
      photoURL
    });
  };
  
  const handleServiceToggle = (serviceId) => {
    setProfessionalForm(prev => {
      const isSelected = prev.serviceIds.includes(serviceId);
      let updatedServiceIds;
      
      if (isSelected) {
        updatedServiceIds = prev.serviceIds.filter(id => id !== serviceId);
      } else {
        updatedServiceIds = [...prev.serviceIds, serviceId];
      }
      
      return {
        ...prev,
        serviceIds: updatedServiceIds
      };
    });
  };
  
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      
      console.log("[ProfessionalManagement] Iniciando salvamento do profissional");
      
      const professionalData = {
        nome: professionalForm.name, // Manter compatibilidade com o campo 'nome' usado no sistema
        name: professionalForm.name,
        email: professionalForm.email,
        phone: professionalForm.phone,
        telefone: professionalForm.phone, // Manter compatibilidade
        bio: professionalForm.bio,
        updatedAt: new Date()
      };
      
      console.log("[ProfessionalManagement] Dados do profissional:", professionalData);
      console.log("[ProfessionalManagement] Serviços selecionados:", professionalForm.serviceIds);
      
      if (currentProfessional) {
        // Atualizar profissional existente
        console.log(`[ProfessionalManagement] Atualizando profissional existente: ${currentProfessional.id}`);
        await updateProfessional(salonId, currentProfessional.id, professionalData);
        
        // Atualizar associações de serviços usando a nova função
        const serviceAssociations = professionalForm.serviceIds.map(serviceId => ({
          serviceId,
          isActive: true
        }));
        
        console.log("[ProfessionalManagement] Atualizando associações:", {
          count: serviceAssociations.length,
          professionalId: currentProfessional.id,
          salonId
        });
        
        const result = await batchUpdateProfessionalServiceAssociations(
          salonId, 
          currentProfessional.id, 
          serviceAssociations
        );
        
        console.log("[ProfessionalManagement] Resultado da atualização de associações:", result);
        
        // Atualizar lista de profissionais - Incluir serviceCount
        setProfessionals(prev => prev.map(p => 
          p.id === currentProfessional.id ? 
          { 
            ...p, 
            ...professionalData,
            serviceCount: professionalForm.serviceIds.length // Atualizar serviceCount no estado
          } : p
        ));
      } else {
        // Adicionar novo profissional
        console.log("[ProfessionalManagement] Adicionando novo profissional");
        const newProfessional = await addProfessional(salonId, {
          ...professionalData,
          serviceCount: 0, // Iniciar com zero e atualizar após criar associações
          createdAt: new Date()
        });
        
        console.log("[ProfessionalManagement] Novo profissional criado:", newProfessional);
        
        // Adicionar associações de serviços usando a nova função
        if (professionalForm.serviceIds.length > 0) {
          const serviceAssociations = professionalForm.serviceIds.map(serviceId => ({
            serviceId,
            isActive: true
          }));
          
          console.log("[ProfessionalManagement] Criando associações para novo profissional:", {
            count: serviceAssociations.length,
            professionalId: newProfessional.id,
            salonId
          });
          
          const result = await batchUpdateProfessionalServiceAssociations(
            salonId, 
            newProfessional.id, 
            serviceAssociations
          );
          
          console.log("[ProfessionalManagement] Resultado da criação de associações:", result);
          
          // Atualizar o serviceCount localmente após criar associações
          newProfessional.serviceCount = professionalForm.serviceIds.length;
          
          // Atualizar o profissional no banco com o serviceCount correto
          await updateProfessional(salonId, newProfessional.id, {
            serviceCount: professionalForm.serviceIds.length
          });
        }
        
        // Atualizar lista de profissionais
        setProfessionals(prev => [...prev, newProfessional]);
      }
      
      // Fechar formulário e resetar
      setShowForm(false);
      setCurrentProfessional(null);
      
      console.log("[ProfessionalManagement] Profissional salvo com sucesso");
    } catch (err) {
      console.error('[ProfessionalManagement] Erro ao salvar profissional:', err);
      setError('Falha ao salvar profissional: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleFormCancel = () => {
    setShowForm(false);
    setCurrentProfessional(null);
  };
  
  // Obter os serviços de um profissional
  const getProfessionalDisplayServices = (serviceIds) => {
    return allServices.filter(service => serviceIds.includes(service.id));
  };

  // Agrupar serviços por categoria para exibição
  const servicesByCategory = allServices.reduce((acc, service) => {
    if (!acc[service.categoryName]) {
      acc[service.categoryName] = [];
    }
    acc[service.categoryName].push(service);
    return acc;
  }, {});

  const handleSendInvite = async (professional) => {
    if (!professional.email) {
      showToast("É necessário um email para enviar o convite", "error");
      return;
    }
    
    try {
      setSendingInvite(true);
      
      // Verificar se já existe um convite para este email
      const existingInvitation = await getUserInvitationByEmail(salonId, professional.email);
      
      if (existingInvitation) {
        showToast(`Já existe um convite pendente para ${professional.email}`, "info");
        setSendingInvite(false);
        return;
      }
      
      // Dados do profissional para o convite
      const professionalData = {
        specialty: professional.specialty || "",
        bio: professional.bio || "",
        name: professional.name || professional.nome || ""
      };
      
      // Criar convite
      const invitation = await createUserInvitation(
        salonId,
        professional.email,
        'professional',
        [],
        professionalData
      );
      
      // Garantir que o token foi devolvido corretamente
      if (!invitation || !invitation.token) {
        throw new Error("Erro ao gerar token de convite");
      }
      
      // Atualizar profissional com status de convite enviado
      await updateProfessional(salonId, professional.id, {
        inviteSent: true,
        inviteSentAt: new Date(),
        invitationToken: invitation.token
      });
      
      // Atualizar lista de profissionais
      setProfessionals(prev => prev.map(p => 
        p.id === professional.id ? { 
          ...p, 
          inviteSent: true, 
          inviteSentAt: new Date(),
          invitationToken: invitation.token 
        } : p
      ));
      
      // Atualizar lista de pendentes
      await fetchPendingProfessionals();
      
      showToast(`Convite enviado para ${professional.email}`, "success");
    } catch (err) {
      console.error('[ProfessionalManagement] Erro ao enviar convite:', err);
      showToast(`Erro ao enviar convite: ${err.message}`, "error");
    } finally {
      setSendingInvite(false);
    }
  };
  
  // Função para copiar link de convite para área de transferência
  const copyInviteLink = (token, email) => {
    try {
      const inviteLink = `${window.location.origin}/accept-invite/${token}?email=${encodeURIComponent(email)}`;
      navigator.clipboard.writeText(inviteLink);
      showToast("Link de convite copiado para área de transferência", "success");
    } catch (err) {
      console.error('[ProfessionalManagement] Erro ao copiar link:', err);
      showToast("Erro ao copiar link. Tente novamente.", "error");
    }
  };
  
  const handleCancelInvite = async (invitation) => {
    try {
      if (!window.confirm(`Deseja realmente cancelar o convite para ${invitation.email}?`)) {
        return;
      }
      
      setSendingInvite(true);
      
      // Importar sob demanda para evitar dependência circular
      const { cancelInvitation } = await import('../../firebase/invitations');
      
      await cancelInvitation(invitation.id);
      
      // Remover convite da lista de pendentes
      setPendingProfessionals(prev => prev.filter(p => p.id !== invitation.id));
      
      showToast(`Convite para ${invitation.email} cancelado`, "success");
    } catch (err) {
      console.error('[ProfessionalManagement] Erro ao cancelar convite:', err);
      showToast(`Erro ao cancelar convite: ${err.message}`, "error");
    } finally {
      setSendingInvite(false);
    }
  };
  
  const handleResendInvite = async (invitation) => {
    try {
      setSendingInvite(true);
      
      // Importar sob demanda para evitar dependência circular
      const { resendInvitation } = await import('../../firebase/invitations');
      
      await resendInvitation(invitation.id);
      
      // Atualizar lista de pendentes
      await fetchPendingProfessionals();
      
      showToast(`Convite reenviado para ${invitation.email}`, "success");
    } catch (err) {
      console.error('[ProfessionalManagement] Erro ao reenviar convite:', err);
      showToast(`Erro ao reenviar convite: ${err.message}`, "error");
    } finally {
      setSendingInvite(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
            <button
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'active'
                  ? 'bg-white shadow text-gray-900'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
              onClick={() => setActiveTab('active')}
            >
              Profissionais
              {professionals.length > 0 && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {professionals.length}
                </span>
              )}
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'pending'
                  ? 'bg-white shadow text-gray-900'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
              onClick={() => setActiveTab('pending')}
            >
              Pendentes
              {pendingProfessionals.length > 0 && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  {pendingProfessionals.length}
                </span>
              )}
            </button>
          </div>
          
          {!showForm && (
            <Button 
              onClick={handleAddProfessional}
              disabled={loading}
            >
              Adicionar Profissional
            </Button>
          )}
        </div>
        
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
            {error}
          </div>
        )}
        
        {loading && !showForm && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        )}

        {!showForm ? (
          <div className="mb-8">
            {activeTab === 'active' && (
              <>
                {professionals.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {professionals.map((professional) => (
                      <div key={professional.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                        <div className="p-4 border-b border-gray-100 flex justify-between items-center">
                          <h3 className="text-lg font-medium text-gray-900">
                            {professional.nome || professional.name}
                          </h3>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditProfessional(professional)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteProfessional(professional)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="p-4">
                          {professional.email && (
                            <div className="mb-2 text-sm">
                              <span className="font-medium text-gray-600">Email:</span> {professional.email}
                            </div>
                          )}
                          {(professional.phone || professional.telefone) && (
                            <div className="mb-2 text-sm">
                              <span className="font-medium text-gray-600">Telefone:</span> {professional.phone || professional.telefone}
                            </div>
                          )}
                          
                          {professional.email && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              {professional.inviteSent ? (
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center text-sm">
                                    <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                    <span className="text-green-600">Convite enviado</span>
                                  </div>
                                  {professional.invitationToken && (
                                    <button
                                      onClick={() => copyInviteLink(professional.invitationToken, professional.email)}
                                      title="Copiar link de convite"
                                      className="text-blue-600 hover:text-blue-800 text-sm"
                                    >
                                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleSendInvite(professional)}
                                  disabled={sendingInvite}
                                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                                >
                                  {sendingInvite ? (
                                    <>
                                      <svg className="animate-spin h-4 w-4 mr-2 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                      Enviando...
                                    </>
                                  ) : (
                                    <>
                                      <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                      </svg>
                                      Enviar convite
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-gray-600">
                      Nenhum profissional cadastrado. Clique em "Adicionar Profissional" para começar.
                    </p>
                  </div>
                )}
              </>
            )}
            
            {activeTab === 'pending' && (
              <>
                {pendingProfessionals.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Nome
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Enviado em
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Ações
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {pendingProfessionals.map((invitation) => {
                          // Definir nome do profissional (pode vir de diferentes lugares)
                          const name = invitation.professionalData?.name || 
                                      invitation.professionalData?.nome || 
                                      "Sem nome";
                          
                          return (
                            <tr key={invitation.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {invitation.email}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {invitation.createdAt.toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Pendente
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                <button
                                  type="button"
                                  onClick={() => copyInviteLink(invitation.token, invitation.email)}
                                  title="Copiar link de convite"
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  <svg className="h-4 w-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                  Link
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleResendInvite(invitation)}
                                  disabled={sendingInvite}
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  Reenviar
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleCancelInvite(invitation)}
                                  disabled={sendingInvite}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  Cancelar
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-gray-600">
                      Nenhum convite pendente.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8 animate-fadeIn">
            <h3 className="text-lg font-medium text-gray-900 mb-6">
              {currentProfessional ? 'Editar Profissional' : 'Novo Profissional'}
            </h3>
            
            <form onSubmit={handleFormSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="md:col-span-2 space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Nome completo
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={professionalForm.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={professionalForm.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone
                    </label>
                    <input
                      type="text"
                      id="phone"
                      name="phone"
                      value={professionalForm.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-1">
                      Biografia (opcional)
                    </label>
                    <textarea
                      id="bio"
                      name="bio"
                      rows="3"
                      value={professionalForm.bio}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Uma breve descrição sobre o profissional..."
                    ></textarea>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Foto (opcional)
                  </label>
                  <div className="mt-1 flex flex-col items-center">
                    <div className="h-32 w-32 relative rounded-full overflow-hidden bg-gray-100 mb-4">
                      {professionalForm.photoURL ? (
                        <img 
                          src={professionalForm.photoURL} 
                          alt="Preview" 
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center bg-gray-100 text-gray-300">
                          <svg className="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <label
                      htmlFor="photo-upload"
                      className="cursor-pointer px-3 py-2 bg-white border border-gray-300 rounded-md font-medium text-gray-700 hover:bg-gray-50"
                    >
                      {professionalForm.photo ? 'Alterar foto' : 'Adicionar foto'}
                      <input
                        type="file"
                        id="photo-upload"
                        className="sr-only"
                        accept="image/*"
                        onChange={handlePhotoChange}
                      />
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  Serviços que este profissional realiza
                </h4>
                
                <div className="space-y-4 max-h-64 overflow-y-auto pr-2">
                  {Object.entries(servicesByCategory).map(([categoryName, services]) => (
                    <div key={categoryName}>
                      <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        {categoryName}
                      </h5>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        {services.map((service) => (
                          <div key={service.id} className="flex items-start py-2">
                            <input
                              id={`service-${service.id}`}
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                              checked={professionalForm.serviceIds.includes(service.id)}
                              onChange={() => handleServiceToggle(service.id)}
                            />
                            <label htmlFor={`service-${service.id}`} className="ml-3">
                              <span className="block text-sm font-medium text-gray-700">{service.name}</span>
                              <span className="block text-xs text-gray-500">
                                {service.duration} min • R$ {service.price}
                              </span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleFormCancel}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="inline-block animate-spin h-4 w-4 mr-2 border-t-2 border-white rounded-full"></span>
                      Salvando...
                    </>
                  ) : (
                    'Salvar'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfessionalManagement; 